import { effect, inject, Injectable } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { asyncScheduler, Subject, tap, throttleTime } from "rxjs";

import type { IBaseCard, IDashboardModel, ITranslateObject, JobsBillingCard } from "@app/views-interfaces";
import { isiFirmTax, isJobsBilling, isTaxprep } from "@app/views-interfaces";
import type { IFirmUser } from "@interfaces/index";
import { AnalyticsService } from "@shared/analytics-service";
import { CardDisplayService } from "@shared/card-display";
import { CardViewService } from "@shared/card-view-service";
import { DashboardsService } from "@shared/dashboards-service";
import { HelpersService } from "@shared/helpers-service";
import { IFirmContactsService } from "@shared/ifirm-contacts-service";
import { IfirmTaxCardsService } from "@shared/ifirm-tax-cards-service";
import { JobsAndBillingCommonService } from "@shared/jobs-and-billing-common-service";

export interface IFirmUserWithId extends IFirmUser {
    Id: string;
}
@Injectable({ providedIn: "root" })
export class FiltersService {
    private readonly dashboardsService = inject(DashboardsService);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly ifirmTaxCardsService = inject(IfirmTaxCardsService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly iFirmContactsService = inject(IFirmContactsService);
    public readonly cardViewService = inject(CardViewService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);

    private readonly filtersChangedEmitter = new Subject<boolean>();
    public filtersChangedEvent$ = this.filtersChangedEmitter.asObservable();
    private currentDashboard?: IDashboardModel;
    private readonly skipFilterChange = { Filtertype: "", value: false };
    private static readonly FILTER_ANALYTICS_EVENT = "Filters are changed";
    // Consolidates rapid-fire handleFilterChange bursts (e.g. cascading async effects) into leading and trailing emissions only.
    private static readonly FILTER_CHANGE_DEBOUNCE_MS = 100;
    private readonly filterChangeRequestEmitter = new Subject<boolean>();

    public activeFilters = new Map<string, (card: IBaseCard) => boolean>();
    public activeFiltersForPrint = new Map<string, { label: string | ITranslateObject; values: (string | ITranslateObject)[] }>();
    public activeSort: ((a: IBaseCard, b: IBaseCard) => number) | undefined;
    public activeSortForPrint: string | undefined;

    constructor() {
        this.resetActiveFiltersAndSort();
        this.refreshFiltersDependencies();

        this.helpersService.refreshFiltersEvent$.pipe(takeUntilDestroyed()).subscribe(async () => {
            await this.helpersService.initializeCategories();
        });

        this.dashboardsService.currentDashboardChanged$.pipe(takeUntilDestroyed()).subscribe(async () => {
            this.resetActiveFiltersAndSort();
            await this.refreshFiltersDependencies();
        });

        effect(() => {
            const currentAvailableDocuments = this.ifirmTaxCardsService.currentAvailableDocuments();
            if (!currentAvailableDocuments) return;

            this.filtersChangedEmitter.next(false);
        });

        effect(() => {
            const currentAccessibleContacts = this.iFirmContactsService.accessibleContacts();
            if (!currentAccessibleContacts) return;

            this.filtersChangedEmitter.next(false);
        });

        this.filterChangeRequestEmitter
            .pipe(
                tap((trackAnalytics) => {
                    if (trackAnalytics) this.trackFilterAnalytics();
                }),
                throttleTime(FiltersService.FILTER_CHANGE_DEBOUNCE_MS, asyncScheduler, { leading: true, trailing: true }),
                takeUntilDestroyed(),
            )
            .subscribe(() => this.filtersChangedEmitter.next(false));
    }

    private resetActiveFiltersAndSort(): void {
        this.activeFilters.clear();
        this.activeFiltersForPrint.clear();
        this.activeSort = undefined;
        this.activeSortForPrint = undefined;
    }

    public async refreshFiltersDependencies() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        await this.helpersService.initializeCategories();
        await this.helpersService.mergeSteps();
    }

    /**
     * Handles changes to the filters, optionally tracks the analytics.
     * Bursts of calls arriving within `FILTER_CHANGE_DEBOUNCE_MS` (e.g. cascading async effects) are
     * coalesced via throttleTime: the first call notifies immediately, the last one in the burst notifies
     * again once things go quiet. Analytics tracking is never dropped, even when the notification is coalesced.
     * @param trackAnalytics - If `true`, analytics for the filter change will be tracked.
     */
    public handleFilterChange(trackAnalytics = false) {
        if (this.skipFilterChange.Filtertype !== "Roles" || (this.skipFilterChange.Filtertype === "Roles" && this.skipFilterChange.value)) {
            this.skipFilterChange.Filtertype = "all";
            this.skipFilterChange.value = false;
            this.filterChangeRequestEmitter.next(trackAnalytics);
        }
    }

    public filterCards(cards: IBaseCard[], statusIds?: string[], options: { ignoreCurrentView?: boolean } = {}): IBaseCard[] {
        const filters = Array.from(this.activeFilters.values());
        return this.filterCardsDisplayable(cards, statusIds, options).filter((card) => filters.every((f) => f(card)));
    }

    public sortCards(cards: IBaseCard[]) {
        const activeSort = this.activeSort;
        if (activeSort) {
            cards.sort((a, b) => activeSort(a, b));
        }
    }

    private filterCardsDisplayable(cards: IBaseCard[], statusIds?: string[], options: { ignoreCurrentView?: boolean } = {}): IBaseCard[] {
        return cards.filter(
            (card) =>
                (!statusIds || statusIds.includes(this.cardDisplayService.getCardDisplayStatusId(card))) &&
                this.isCardAvailableForApplication(card, options),
        );
    }

    public isCardAvailableForApplication(card: IBaseCard, options: { ignoreCurrentView?: boolean } = {}) {
        if (isiFirmTax(this.currentDashboard?.ApplicationType)) {
            return !this.deleteCardByGrouping(card, options) && this.ifirmTaxCardsService.isCardAvailable(card.cardId);
        } else if (isJobsBilling(this.currentDashboard?.ApplicationType)) {
            return this.isJobCardAccessible(card as JobsBillingCard);
        } else if (isTaxprep(this.currentDashboard?.ApplicationType)) {
            return !this.deleteCardByGrouping(card, options);
        } else {
            return true;
        }
    }

    /**
     * Like isCardAvailableForApplication, but for search results: keeps the permission/access
     * checks, but skips deleteCardByGrouping. Search results are not the dashboard grid, so a
     * card that is grouped under its family's main taxpayer card for display purposes should
     * still be returned as its own match here.
     */
    isCardAvailableForSearch(card: IBaseCard) {
        if (isiFirmTax(this.currentDashboard?.ApplicationType)) {
            return this.ifirmTaxCardsService.isCardAvailable(card.cardId);
        } else if (isJobsBilling(this.currentDashboard?.ApplicationType)) {
            return this.isJobCardAccessible(card as JobsBillingCard);
        } else {
            return true;
        }
    }

    private isJobCardAccessible(jobCard: JobsBillingCard) {
        return (
            jobCard.contactInformation.iFirmContactId &&
            this.iFirmContactsService.isContactAccessible(Number.parseInt(jobCard.contactInformation.iFirmContactId, 10)) &&
            this.jobsAndBillingCommonService.hasAccess("view_job", jobCard)
        );
    }

    public deleteCardByGrouping(cardData, options: { ignoreCurrentView?: boolean } = {}): boolean {
        if (!options.ignoreCurrentView && this.cardViewService.currentViewType === "all") return false;
        if (cardData?.return && cardData.return.type !== "Taxpayer") {
            // Card type is not taxpayer
            if (cardData.isSplit !== null) {
                return !cardData.isSplit;
            } else {
                return this.currentDashboard?.DisplayGroupedCards;
            }
        }
        return false;
    }

    private trackFilterAnalytics() {
        const item = localStorage.getItem(`Filter_Selection_${this.currentDashboard?.Id}`);
        const storedSelection = item && item !== "" ? JSON.parse(item) : {};

        if (storedSelection && Object.hasOwn(storedSelection, "SortOrder")) {
            delete storedSelection.SortOrder;
        }
        if (storedSelection && Object.hasOwn(storedSelection, "SortBy")) {
            delete storedSelection.SortBy;
        }
        this.analyticsService.track(FiltersService.FILTER_ANALYTICS_EVENT, { Filters: storedSelection });
    }
}
